import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowDownRightIcon, FacebookIcon, MessageCircleIcon, TruckIcon } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { ProductDialog } from '../components/ProductDialog';
import { ReviewCard } from '../components/ReviewCard';
import { products } from '../data/products';
import { reviews } from '../data/reviews';
import { GENERAL_ENQUIRY_URL, SPECIFIC_FRAGRANCE_URL } from '../utils/whatsapp';
import { trackAddToCart } from '../utils/metaPixel';
import type { Product } from '../types/catalog';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/012b1188-a520-46d8-9ceb-0efe7516bb47.jpg';
const HERO_IMAGE_MOBILE = 'https://cdn.magicpatterns.com/patterns/generated-images/4f09518b-fe98-4b27-bd24-223261549f59.jpg';
const marqueeItems = ['Envío gratis'];
const FACEBOOK_URL = 'https://www.facebook.com/profile.php?id=61592477107032';
const benefits = [{
  title: 'Dura hasta 10 horas',
  body: 'Concentraciones altas y frascos originales. Una pulsación por la mañana sigue ahí en la cena.'
}, {
  title: 'Ingredientes que reconoces',
  body: 'Casas de perfumería consolidadas, con la pirámide olfativa completa publicada en cada ficha.'
}, {
  title: 'Envío seguro y rastreable',
  body: 'Salimos en 24 horas con guía, seguro incluido y 30 días para devolver.'
}];
const easeSmooth = [0.23, 1, 0.32, 1] as const;
export function Home() {
  const [activeProduct, setActiveProduct] = useState<Product | null>(null);
  return <main id="inicio" className="bg-ivory" data-id="element-101" data-mp="2d5w1lvit1oe3">
      <section className="relative overflow-hidden" data-id="element-102" data-mp="287ugz65qdh0i">
        <div className="mx-auto grid max-w-7xl items-center gap-8 px-5 pb-12 pt-12 sm:px-8 lg:grid-cols-[0.95fr_1.05fr] lg:gap-12 lg:pb-20 lg:pt-16" data-id="element-103" data-mp="288drw2wyr551">
          <motion.div initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: easeSmooth
        }} className="max-w-xl" data-id="element-104" data-mp="1z7xnmr8ebtz4">
            

            <h1 className="mt-6 font-display text-[2.9rem] font-extrabold leading-[0.98] tracking-tight text-navy sm:text-6xl lg:text-7xl" data-id="element-105" data-mp="1kmtncsbelnwx">
              No necesitas oler como{' '}
              <span className="text-cobalt" data-id="element-106" data-mp="1ao452yfli1g5">todos.</span>
            </h1>

            <p className="mt-6 max-w-lg font-display text-2xl font-bold leading-tight text-navy sm:text-3xl" data-id="element-107" data-mp="1fmp4ivn0wh3j">
              Perfumes creados para que tu presencia hable antes que tú.
            </p>

            <a href="#perfumes" className="mt-8 inline-flex items-center gap-2 rounded-full bg-lime px-8 py-4 text-sm font-bold uppercase tracking-wide text-navy transition-colors duration-200 ease-smooth hover:bg-lime-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-cobalt" data-id="element-108" data-mp="2899upsrrin5i">
              Encuentra tu aroma
              <ArrowDownRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-109" data-mp="2m8huamru7xa8" />
            </a>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3,
          ease: easeSmooth,
          delay: 0.06
        }} data-id="element-110" data-mp="2r2vwb81092nv">
            <img src={HERO_IMAGE_MOBILE} alt="Ilustración editorial de un frasco de perfume entre cítricos, pétalos y vapor" className="aspect-[4/5] w-full rounded-3xl object-cover lg:hidden" data-id="element-111" data-mp="oedtvkqjlato" />
            <img src={HERO_IMAGE} alt="Ilustración editorial de frascos de perfume entre cítricos, pétalos, anís y vapor" className="hidden aspect-[3/2] w-full rounded-3xl object-cover lg:block" data-id="element-112" data-mp="3srkdvld9cd77" />
          </motion.div>
        </div>
      </section>

      <div className="overflow-hidden bg-navy py-3" aria-hidden="true" data-id="element-113" data-mp="5ljnvgawct1l">
        <div className="marquee-track flex w-max gap-10 whitespace-nowrap" data-id="element-114" data-mp="nryou1mzl13">
          {[...marqueeItems, ...marqueeItems, ...marqueeItems, ...marqueeItems].map((item, index) => <span key={`${item}-${index}`} className="font-display text-sm font-bold uppercase tracking-wide text-ivory" data-id="element-115" data-mp="1c484dvne2nar" data-mpi={index}>
                {item} <span className="text-lime" data-id="element-116" data-mp="27f6k1h29mr63">✦</span>
              </span>)}
        </div>
      </div>

      <section className="mx-auto max-w-5xl px-5 py-16 sm:px-8 lg:py-24" data-id="element-117" data-mp="2cdraiorptwhc">
        <h2 className="font-display text-3xl font-extrabold leading-tight tracking-tight text-navy sm:text-5xl" data-id="element-118" data-mp="2hbjbe8ac8sxo">
          ¿Cansado de perfumes que desaparecen
          <br className="hidden sm:block" data-id="element-119" data-mp="2m8t01lpwl0kg" /> y huelen igual?
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-mute sm:text-lg" data-id="element-120" data-mp="2qc7sxcyxxbfb">
          Uno entra a la sala antes que tú, otro se queda cerca y solo lo descubre quien se
          acerca.{' '}
          <span className="text-cobalt" data-id="element-121" data-mp="3032fkig3md1a">Elige el que quieras ser hoy.</span>
        </p>
      </section>

      <section id="perfumes" className="mx-auto max-w-7xl px-5 pb-8 sm:px-8" aria-labelledby="coleccion" data-id="element-122" data-mp="350vkz99kv30i">
        <div className="flex flex-wrap items-end justify-between gap-3 border-t border-line pt-8" data-id="element-123" data-mp="3a2kv8moz7cnj">
          <h2 id="coleccion" className="font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl" data-id="element-124" data-mp="3e2lf4muugg1z">
            Selección de fragancias
          </h2>
          <p className="inline-flex items-center gap-2 rounded-full bg-lime px-4 py-2 text-xs font-bold uppercase tracking-wide text-navy" data-id="element-125" data-mp="ycbnvwrsri0j">
            <TruckIcon className="h-4 w-4" aria-hidden="true" data-id="element-126" data-mp="tgebu2nkgxtk" />
            Envío totalmente gratis
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-x-5 gap-y-12 sm:grid-cols-2 lg:grid-cols-5" data-id="element-127" data-mp="1m2pk7zhf030x">
          {products.map(product => <ProductCard key={product.slug} product={product} onOpen={setActiveProduct} data-id="element-128" data-mp="1h4xheaf8ewhy" />)}
        </div>

        <div className="mt-14 rounded-3xl bg-navy px-6 py-10 sm:px-10 lg:py-12" data-id="element-129" data-mp="1cc4dtpvi1u7g">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-130" data-mp="17ekwgh3i91ba">
            <div className="max-w-lg" data-id="element-131" data-mp="24tyw2fenr3vo">
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-ivory sm:text-3xl" data-id="element-132" data-mp="24v1juegooq7d">
                ¿Buscas una fragancia específica?
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-ivory/70 sm:text-base" data-id="element-133" data-mp="3ubnc2fkngmq8">
                Cuéntanos cuál tienes en mente y consultamos disponibilidad para ti.
              </p>
            </div>
            <a href={SPECIFIC_FRAGRANCE_URL} target="_blank" rel="noreferrer" onClick={() => trackAddToCart({
            contentName: 'Consulta de fragancia específica'
          })} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-lime px-8 py-4 text-sm font-bold uppercase tracking-wide text-navy transition-colors duration-200 ease-smooth hover:bg-lime-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-134" data-mp="3kjpxcyufncmr">
              <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-135" data-mp="zuk54fubr3ef" />
              Consultar disponibilidad
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pt-16 sm:px-8 lg:pt-24" aria-labelledby="opiniones" data-id="element-136" data-mp="14svd0l1273u0">
        <div className="flex flex-wrap items-end justify-between gap-3 border-t border-line pt-8" data-id="element-137" data-mp="19yh29qabxs35">
          <h2 id="opiniones" className="font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl" data-id="element-138" data-mp="1npvm1zc29xb9">
            Opiniones de clientes
          </h2>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-mute" data-id="element-139" data-mp="1xo2x61jo5l5c">
            Compras verificadas
          </p>
        </div>

        <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-140" data-mp="1so6tk3evvvqb">
          {reviews.map(review => <li key={review.id} className="h-full" data-id="element-141" data-mp="26npyyogb8syx">
              <ReviewCard review={review} data-id="element-142" data-mp="22n64xtr1a5h0" />
            </li>)}
        </ul>
      </section>

      <section className="mt-16 bg-navy lg:mt-24" aria-labelledby="beneficios" data-id="element-143" data-mp="22lbiuc4107bc">
        <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8 lg:py-20" data-id="element-144" data-mp="cb08yht2c3wt">
          <h2 id="beneficios" className="sr-only" data-id="element-145" data-mp="8a6jhjzz2h1t">
            Por qué comprar en Jam Fragrances
          </h2>
          <ul className="grid gap-10 sm:grid-cols-3" data-id="element-146" data-mp="wtjwo3cfng6i">
            {benefits.map((benefit, index) => <li key={benefit.title} data-id="element-147" data-mp="11rv6ie2q9qoq" data-mpi={index}>
                <span className="font-display text-4xl font-extrabold text-lime" data-id="element-148" data-mp="11qtil90m59pl">
                  0{index + 1}
                </span>
                <h3 className="mt-3 font-display text-xl font-bold text-ivory" data-id="element-149" data-mp="1alzv0que2q13">{benefit.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ivory/70" data-id="element-150" data-mp="dvqw37pa7ica">{benefit.body}</p>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-ivory" data-id="element-151" data-mp="iweith6h9d0p">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-6 px-5 py-16 sm:px-8 lg:flex-row lg:items-center lg:justify-between lg:py-20" data-id="element-152" data-mp="mvdwbq69yi3r">
          <h2 className="max-w-xl font-display text-3xl font-extrabold leading-tight tracking-tight text-navy sm:text-5xl" data-id="element-153" data-mp="rwa083qmm496">
            Que te recuerden por algo.
          </h2>
          <div className="flex flex-wrap items-center gap-5" data-id="element-154" data-mp="238u9n74iyv6n">
            <a href={GENERAL_ENQUIRY_URL} target="_blank" rel="noreferrer" onClick={() => trackAddToCart({
            contentName: 'Pedido general (cierre)'
          })} className="inline-flex items-center gap-2 rounded-full bg-lime px-8 py-4 text-sm font-bold uppercase tracking-wide text-navy transition-colors duration-200 ease-smooth hover:bg-lime-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-cobalt" data-id="element-155" data-mp="2ddstjjawrusx">
              <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-156" data-mp="1po872qfjqfew" />
              Pedir por WhatsApp
            </a>
            <a href={FACEBOOK_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-cobalt underline-offset-4 transition-colors duration-200 ease-smooth hover:underline" data-id="element-157" data-mp="1kpwzyngmyj0d">
              <FacebookIcon className="h-4 w-4" aria-hidden="true" data-id="element-158" data-mp="3b2m2kjyt3mja" />
              Jam Fragrances
            </a>
          </div>
        </div>
      </section>

      <ProductDialog product={activeProduct} onClose={() => setActiveProduct(null)} data-id="element-159" data-mp="8nrkfskg00oi" />
    </main>;
}